/**
 * 历史记录工具类 - 负责本地存储的读写操作
 */
export const HistoryUtils = {
  // 本地存储的键名
  STORAGE_KEY: 'aes_encrypt_history',
  
  /**
   * 检查localStorage是否可用
   * @returns {boolean} 是否可用
   */
  isStorageAvailable: () => {
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.error('localStorage不可用:', e);
      return false;
    }
  },
  
  /**
   * 生成唯一ID
   * @returns {string} 唯一ID
   */
  generateUniqueId: () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },
  
  /**
   * 获取所有历史记录
   * @returns {Array} 历史记录数组
   */
  getHistory: () => {
    if (!HistoryUtils.isStorageAvailable()) {
      console.warn('localStorage不可用，无法获取历史记录');
      return [];
    }
    
    try {
      const historyData = localStorage.getItem(HistoryUtils.STORAGE_KEY);
      
      // 检查数据是否为有效的JSON字符串
      if (historyData) {
        const parsedData = JSON.parse(historyData);
        if (Array.isArray(parsedData)) {
          // 按时间戳倒序排序
          return parsedData.sort((a, b) => {
            const timeA = new Date(a.timestamp || 0).getTime();
            const timeB = new Date(b.timestamp || 0).getTime();
            return timeB - timeA;
          });
        } else {
          console.warn('历史记录数据格式不正确，应为数组');
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error('获取历史记录失败:', error);
      
      // 如果解析失败，尝试清除可能已损坏的数据
      try {
        localStorage.removeItem(HistoryUtils.STORAGE_KEY);
      } catch (e) {
        // 忽略清除时的错误
      }
      
      return [];
    }
  },
  
  /**
   * 添加一条历史记录
   * @param {Object} record - 历史记录对象
   * @param {string} record.type - 操作类型：'加密' 或 '解密'
   * @param {string} record.paddingMode - 填充模式
   * @param {string} record.keyLength - 密钥长度
   * @param {string} record.key - 密钥
   * @param {string} record.iv - 初始向量
   * @param {string} record.inputText - 输入内容
   * @param {string} record.result - 结果内容
   * @returns {boolean} 操作是否成功
   */
  addHistory: (record) => {
    if (!HistoryUtils.isStorageAvailable()) {
      console.warn('localStorage不可用，无法添加历史记录');
      return false;
    }
    
    try {
      // 校验必填字段
      if (!record || !record.type || !record.result) {
        console.warn('添加历史记录失败: 缺少必要字段');
        return false;
      }
      
      // 获取现有历史记录
      const history = HistoryUtils.getHistory();
      
      // 创建新记录，添加唯一ID和时间戳
      const newRecord = {
        ...record,
        id: HistoryUtils.generateUniqueId(),
        timestamp: new Date().toISOString(),
        time: new Date().toLocaleString('zh-CN') // 格式化为中文日期格式
      };
      
      // 将新记录添加到历史记录数组的开头
      history.unshift(newRecord);
      
      // 限制历史记录数量（最多保存20条）
      const limitedHistory = history.slice(0, 20);
      
      // 保存到本地存储
      localStorage.setItem(HistoryUtils.STORAGE_KEY, JSON.stringify(limitedHistory));
      console.log('历史记录添加成功:', newRecord);
      return true;
    } catch (error) {
      console.error('添加历史记录失败:', error);
      return false;
    }
  },
  
  /**
   * 删除一条历史记录
   * @param {string} id - 记录ID
   * @returns {boolean} 操作是否成功
   */
  deleteHistory: (id) => {
    if (!HistoryUtils.isStorageAvailable()) {
      console.warn('localStorage不可用，无法删除历史记录');
      return false;
    }
    
    try {
      // 获取现有历史记录
      const history = HistoryUtils.getHistory();
      
      // 验证ID是否存在
      if (!history.some(item => item.id === id)) {
        console.warn(`删除历史记录失败: ID ${id} 不存在`);
        return false;
      }
      
      // 过滤掉要删除的记录
      const updatedHistory = history.filter(item => item.id !== id);
      
      // 保存到本地存储
      localStorage.setItem(HistoryUtils.STORAGE_KEY, JSON.stringify(updatedHistory));
      return true;
    } catch (error) {
      console.error('删除历史记录失败:', error);
      return false;
    }
  },
  
  /**
   * 清空所有历史记录
   * @returns {boolean} 操作是否成功
   */
  clearHistory: () => {
    if (!HistoryUtils.isStorageAvailable()) {
      console.warn('localStorage不可用，无法清空历史记录');
      return false;
    }
    
    try {
      // 直接移除存储项
      localStorage.removeItem(HistoryUtils.STORAGE_KEY);
      
      // 验证是否清空成功
      const currentData = localStorage.getItem(HistoryUtils.STORAGE_KEY);
      if (currentData !== null) {
        // 如果移除失败，尝试设置为空数组
        localStorage.setItem(HistoryUtils.STORAGE_KEY, '[]');
        return localStorage.getItem(HistoryUtils.STORAGE_KEY) === '[]';
      }
      return true;
    } catch (error) {
      console.error('清空历史记录失败:', error);
      return false;
    }
  }
};

export default HistoryUtils; 